export class CirclePlane extends THREE.Group {
    constructor(pickingData, modelId) {
        super();
        if (pickingData.length !== 3) {
            throw new Error('Insufficient information to create a model.');
        }

        this.model = getModelObject(modelId);

        this.perimeterPoints = [];

        pickingData.forEach(pickingDatum => {
            if (pickingDatum.modelId !== modelId) {
                throw new Error(`All the points should be from the same model.`);
            }

            this.perimeterPoints.push({
                point: pickingDatum.point.clone(),
                branchID: pickingDatum.volume.branchID,
            });
        });

        this.updateModelCallback = this.updateModel.bind(this);
        this.model.addEventListener('modelMoved', this.updateModelCallback);

        this.updateVolumeCallback = this.updateVolume.bind(this);
        this.model.addEventListener('volumeMoved', this.updateVolumeCallback);

        this.getCircleData();

        this.sceneSize = new THREE.Vector3();
        getModelBoundingBox().getSize(this.sceneSize);
        this.sceneSize = this.sceneSize.length() * 0.001;

        this.createCircleObject();
        this.normalObject = this.createNormalObject();
    }

    getCircleData() {
        const circle = getCircleDataFromThreePoints(this.perimeterPoints.map(({point}) => point.clone()));
        if (circle.status === CircleFromPoints.CircleResult.UNIQUE_SOLUTION) {
            this.centerPoint = circle.circleCenter;
            this.normal = circle.direction;

            const modelSpacePoints = this.perimeterPoints
                .map(({point, branchID}) =>
                    getOrgPosition(this.model.worldToLocal(point.clone()), this.model.modelId, branchID));
            const modelSpaceCircleData = getCircleDataFromThreePoints(modelSpacePoints);
            if (modelSpaceCircleData.status !== CircleFromPoints.CircleResult.UNIQUE_SOLUTION) {
                throw new Error("Circle can't be formed from the selected points.");
            }
            this.normalInModelSpace = modelSpaceCircleData.direction;
        } else {
            throw new Error("Circle can't be formed from the selected points.");
        }
    }

    updatePointAndNormal(offsetMatrix = new THREE.Matrix4(), branchID = null) {
        for (let pointData of this.perimeterPoints) {
            if (!branchID) {
                pointData.point.applyMatrix4(offsetMatrix);
            } else if (pointData.branchID === branchID) {
                pointData.point.applyMatrix4(offsetMatrix);
            }
        }

        this.getCircleData();

        this.centerPointObject.position.copy(this.centerPoint);
        this.centerPointObject.material.color.setHex(AlignmentPlane.CANDIDATE_COLOR);
        this.centerPointObject.add(this.normalObject);
        this.normalObject.setDirection(this.normal);
        this.normalObject.setLength(this.sceneSize * AlignmentPlane.ARROW_MULTIPLIER);
        this.createCirclePerimeter();
    }

    updateModel(event) {
        let offsetMatrix = event.matrixWorldAfter.clone().multiply(event.matrixWorldBefore.clone().invert());
        this.updatePointAndNormal(offsetMatrix);
    }

    updateVolume(event) {
        if (this.perimeterPoints.every(({branchID}) => branchID !== event.branchId)) {
            return;
        }

        let offsetMatrix = event.matrixWorldAfter.clone().multiply(event.matrixWorldBefore.clone().invert());
        this.updatePointAndNormal(offsetMatrix, event.branchId);
    }

    createCircleObject() {
        this.centerPointObject = new THREE.Mesh(new THREE.SphereGeometry(this.sceneSize), new THREE.MeshBasicMaterial({
            color: AlignmentPlane.CANDIDATE_COLOR,
            transparent: true,
            opacity: 0.5
        }));

        this.centerPointObject.add(new THREE.Mesh(new THREE.SphereGeometry(this.sceneSize * 0.75), new THREE.MeshBasicMaterial({
            color: 0xffff00
        })));

        this.centerPointObject.position.copy(this.centerPoint);

        this.createCirclePerimeter();

        this.add(this.centerPointObject);

        return this.centerPointObject;
    }

    createCirclePerimeter() {
        if (this.circlePerimeterObject) {
            this.circlePerimeterObject.parent?.remove(this.circlePerimeterObject);
            disposeObject(this.circlePerimeterObject);
        }

        this.circlePerimeterObject = BasicShapes.createCircle(
            this.centerPoint,
            this.perimeterPoints[0].point,
            this.normal,
            new THREE.Vector3(0, 0, 0)
        );
        this.circlePerimeterObject.position.copy(new THREE.Vector3(0, 0, 0));
        this.centerPointObject.add(this.circlePerimeterObject);
    }

    createNormalObject() {
        let object = new THREE.ArrowHelper(this.normal, new THREE.Vector3(), this.sceneSize * AlignmentPlane.ARROW_MULTIPLIER);
        this.centerPointObject.add(object);
        return object;
    }

    getNormal() {
        return {
            normal: this.normalInModelSpace,
        };
    }

    reverseNormal() {
        this.normal.negate();
        this.normalInModelSpace.negate();
        this.normalObject.setDirection(this.normal);
    }

    destroy() {
        if (this.parent) {
            this.parent.remove(this);
        }

        this.model.removeEventListener('modelMoved', this.updateModelCallback);
        this.model.removeEventListener('volumeMoved', this.updateVolumeCallback);

        disposeObject(this);
    }
}
